import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import ScrollReveal from '../../../../components/ScrollReveal';
import heroBgImage from '../../../../images/services/vapt/shield-hero.webp';
import ctaBgImage from '../../../../images/cta/cta-background.webp';
import { Shield, FileText, Search, AlertTriangle, CheckCircle, Settings, Users, Award, RefreshCw, FileCheck, Eye, Database, Globe, Target, ArrowRight, Landmark } from 'lucide-react';
import './SOC1.css';

const challenges = [
    { icon: <Target size={24} />, title: 'Financial Reporting Controls Scoping', desc: 'Identifying which IT general controls, application controls, and business process controls are in scope for the SOC 1 examination as they relate to user entities\' financial reporting.' },
    { icon: <FileText size={24} />, title: 'Control Design vs. Operating Effectiveness', desc: 'SOC 1 Type II requires demonstrating that controls not only exist but operated effectively over the review period — demanding rigorous documentation and continuous evidence collection.' },
    { icon: <Users size={24} />, title: 'User Entity Control Responsibilities', desc: 'Documenting complementary user entity controls (CUECs) and ensuring the SOC 1 report clearly communicates what customers must implement for controls to be effective.' },
    { icon: <Database size={24} />, title: 'Evidence Collection & Documentation', desc: 'Systematically collecting and organizing evidence of control operation across the entire audit period without disrupting operational workflows.' },
    { icon: <AlertTriangle size={24} />, title: 'Change Management & Exceptions', desc: 'Managing control exceptions, deviations, and changes to the control environment during the SOC 1 audit period without triggering material exceptions in the final report.' },
    { icon: <RefreshCw size={24} />, title: 'Annual Audit Cycle Management', desc: 'Maintaining continuous readiness for annual SOC 1 renewals and managing the relationship with the independent CPA firm conducting the examination.' },
];

const components = [
    { icon: <Target size={22} />, title: 'Scope Definition', desc: 'Defining the SOC 1 service description scope — covering in-scope systems, controls relevant to financial reporting, and CUEC documentation.' },
    { icon: <FileText size={22} />, title: 'Control Environment Assessment', desc: 'Structured assessment of existing ITGC and application controls against SSAE 18 requirements, identifying design gaps and operating effectiveness weaknesses.' },
    { icon: <Settings size={22} />, title: 'Control Design & Implementation', desc: 'Design and implementation of financial reporting-relevant controls across IT operations, change management, access, availability, and processing integrity.' },
    { icon: <Eye size={22} />, title: 'Evidence Collection Framework', desc: 'Systematic evidence collection framework ensuring continuous capture of control operation evidence across the entire SOC 1 audit period.' },
    { icon: <FileCheck size={22} />, title: 'Readiness Assessment', desc: 'Pre audit readiness assessment simulating auditor testing of control design and operating effectiveness eliminating surprises in the formal examination.' },
    { icon: <Award size={22} />, title: 'Auditor Liaison & Management', desc: 'Selection of qualified CPA firm, engagement management, auditor request fulfilment, and management response to any identified exceptions.' },
];

const approach = [
    { icon: <Search size={22} />, title: 'Readiness Assessment', desc: 'Baseline assessment of current control environment against SOC 1/SSAE 18 requirements — identifying design gaps and operating effectiveness weaknesses.' },
    { icon: <Target size={22} />, title: 'Service Description & Scope', desc: 'Develop the SOC 1 system description covering infrastructure, software, people, processes, and controls relevant to financial reporting.' },
    { icon: <FileText size={22} />, title: 'Control Inventory Development', desc: 'Comprehensive inventory of all controls within scope ITGC, application controls, business process controls with control owners and testing procedures.' },
    { icon: <AlertTriangle size={22} />, title: 'Control Gap Remediation', desc: 'Remediate all control design gaps identified in the readiness assessment before the formal SOC 1 examination period begins.' },
    { icon: <Database size={22} />, title: 'Evidence Collection Programme', desc: 'Implement systematic evidence collection workflows for all in-scope controls, ensuring auditable documentation throughout the examination period.' },
    { icon: <Users size={22} />, title: 'CUEC Documentation', desc: 'Document complementary user entity controls clearly and accurately, ensuring customers understand their responsibilities for SOC 1 controls to be effective.' },
    { icon: <Settings size={22} />, title: 'Change & Exception Management', desc: 'Establish procedures for managing control environment changes and tracking exceptions during the audit period to minimize report qualifications.' },
    { icon: <Eye size={22} />, title: 'Pre Audit Readiness Testing', desc: 'Simulate auditor testing of control operating effectiveness across the examination period, identifying and remediating remaining weaknesses.' },
    { icon: <FileCheck size={22} />, title: 'Auditor Management & Support', desc: 'Manage the CPA firm relationship, coordinate auditor information requests, and provide guidance on management responses to findings.' },
    { icon: <Award size={22} />, title: 'Report Issuance & Customer Support', desc: 'Review and finalize SOC 1 report content, management representation letter, and establish secure distribution process for customer delivery.' },
];

const checklist = [
    'SOC 1/SSAE 18 readiness assessment against financial reporting control requirements',
    'Service description development covering all in-scope systems and processes',
    'Control inventory covering ITGC, application controls, and business process controls',
    'Control design gap identification and remediation before examination period',
    'Evidence collection framework and documentation procedures for all controls',
    'Complementary user entity controls (CUEC) documentation',
    'Change management controls for control environment modifications during audit period',
    'Exception and deviation tracking and management procedures',
    'Pre audit readiness testing simulating CPA firm auditor testing',
    'CPA firm selection and engagement management',
    'Type I or Type II examination coordination and auditor request fulfilment',
    'SOC 1 report review and management response to any identified exceptions',
];

const why = [
    { icon: <Award size={36} strokeWidth={1.5} />, title: 'SOC Examination Specialists', desc: 'Our team has deep SOC 1 examination experience understanding exactly how CPA firms test controls and what evidence they require for a clean Type II report.' },
    { icon: <Shield size={36} strokeWidth={1.5} />, title: 'Complete Readiness Programme', desc: 'Our pre examination readiness programme eliminates surprises we simulate auditor testing before the formal examination, ensuring control operating effectiveness.' },
    { icon: <Globe size={36} strokeWidth={1.5} />, title: 'Ongoing Audit Cycle Support', desc: 'From initial Type I through annual Type II renewals, we provide continuous SOC 1 support — evidence management, control maintenance, and auditor liaison.' },
];

const trad = [
    'Generic control frameworks not tailored to financial reporting relevance requirements.',
    'Evidence collection left to client operations teams without structured procedures.',
    'CUECs documented superficially, creating ambiguity about customer responsibilities.',
    'No pre audit readiness testing, leading to exceptions discovered during CPA examination.',
    'Control gaps not remediated before examination period begins, affecting Type II opinions.',
    'No ongoing support between annual audits, leaving control gaps to accumulate.',
];
const ca = [
    'Control scope precisely mapped to financial reporting relevance for your service model.',
    'Structured evidence collection framework ensuring continuous documentation throughout audit period.',
    'CUECs clearly and specifically documented with customer implementation guidance.',
    'Rigorous pre audit readiness testing eliminating CPA examination surprises.',
    'Control gaps identified and remediated before examination period commencement.',
    'Continuous between audit support maintaining control readiness year round.',
];

const deliverables = [
    { icon: <FileText size={28} />, title: 'Documentation', items: ['System Description Document', 'Control Inventory with Testing Procedures', 'CUEC Documentation', 'Exception & Change Management Log'] },
    { icon: <Eye size={28} />, title: 'Readiness & Testing', items: ['Control Design Assessment Report', 'Pre Audit Readiness Testing Report', 'Control Gap Remediation Evidence', 'Evidence Collection Framework'] },
    { icon: <Settings size={28} />, title: 'Controls Implementation', items: ['ITGC Implementation & Evidence', 'Access Control & Logical Security', 'Change Management Controls', 'Availability & Processing Integrity Controls'] },
    { icon: <Award size={28} />, title: 'Audit Support', items: ['CPA Firm Selection Guidance', 'Auditor Request Management', 'Management Representation Support', 'Annual Renewal Readiness Plan'] },
];

const SOC1 = () => {
    const navigate = useNavigate();
    useEffect(() => { window.scrollTo(0, 0); }, []);

    // Parallax effect
    const heroRef = useRef(null);
    const [isDesktop, setIsDesktop] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia('(min-width: 1025px)');
        const update = () => setIsDesktop(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
    const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 70]);

    return (
        <div className="soc1-page">
            {/* HERO SECTION */}
            <section className="soc1-hero" ref={heroRef}>
                <motion.div
                    className="soc1-hero-bg-layer"
                    style={{
                        backgroundImage: `url(${heroBgImage})`,
                        y: isDesktop ? parallaxY : 0
                    }}
                />
                <div className="soc1-hero-overlay" />
                <div className="soc1-hero-glow" />

                {/* Floating particles */}
                <div className="soc1-hero-particles" aria-hidden="true">
                    {Array.from({ length: 7 }).map((_, i) => (
                        <span key={i} className="soc1-hero-particle" style={{ '--i': i }} />
                    ))}
                </div>

                <div className="soc1-container">
                    <div className="soc1-hero-inner">
                        <div className="soc1-hero-content">
                            <ScrollReveal animation="fade-down" delay={0}>
                                <div className="soc1-hero-badge">
                  <Landmark size={14} className="soc1-badge-icon" />
                  <span className="soc1-badge-text">
                    COMPLIANCE <span className="soc1-badge-divider">/</span> GLOBAL ATTESTATIONS
                  </span>
                </div>
                            </ScrollReveal>

                            <ScrollReveal animation="fade-up" delay={100}>
                                <h1 className="soc1-hero-title">
                                    <span className="soc1-grad">SOC 1 ATTESTATION REPORT</span>
                                </h1>
                            </ScrollReveal>

                            <ScrollReveal animation="fade-up" delay={200}>
                                <p className="soc1-hero-description">
                                    A <strong>SOC 1 (SSAE 18) report</strong> provides independent assurance that your organization's controls over financial reporting are designed and operating effectively. It is a critical requirement for service organizations payment processors, IT service providers, and BPOs whose systems affect user entities' financial statements.
                                </p>
                                <p className="soc1-hero-description">
                                    Our SOC 1 practice provides <strong>complete examination support</strong> from readiness assessment and control gap remediation through evidence collection, pre audit testing, CPA firm liaison, and annual renewal management.
                                </p>
                            </ScrollReveal>

                            <ScrollReveal animation="fade-up" delay={300}>
                                <div className="soc1-hero-actions">
                                    <button className="soc1-btn soc1-btn-primary" onClick={() => navigate('/contact')}>
                                        Schedule a SOC 1 Readiness Assessment
                                    </button>
                                </div>
                            </ScrollReveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION A: OFF-WHITE */}
            <section className="soc1-sec soc1-bg-offwhite">
                <div className="soc1-container">
                    <ScrollReveal direction="up">
                        <div className="soc1-hdr">
                            <p className="soc1-eyebrow-red">Our Challenges</p>
                            <h2 className="soc1-title">SOC 1 Examination Challenges</h2>
                            <p className="soc1-desc">Service organizations face significant challenges achieving a clean SOC 1 Type II opinion with minimal exceptions or qualifications</p>
                        </div>
                    </ScrollReveal>
                    <div className="soc1-challenges-grid">
                        {challenges.map((c, i) => (
                            <ScrollReveal key={i} direction="up" delay={i * 80}>
                                <div className="soc1-challenge-card">
                                    <div className="soc1-ch-num">{String(i + 1).padStart(2, '0')}</div>
                                    <div className="soc1-ch-content">
                                        <div className="soc1-ch-header">
                                            <div className="soc1-ch-icon-box">{c.icon}</div>
                                            <h3 className="soc1-ch-title">{c.title}</h3>
                                        </div>
                                        <p className="soc1-ch-desc">{c.desc}</p>
                                    </div>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION B: WHITE + SUBTLE GRID */}
            <section className="soc1-sec soc1-bg-white-grid">
                <div className="soc1-grid-overlay" />
                <div className="soc1-container">
                    <ScrollReveal direction="up">
                        <div className="soc1-hdr">
                            <p className="soc1-eyebrow-red">Our Framework</p>
                            <h2 className="soc1-title">Our SOC 1 Examination Framework</h2>
                            <p className="soc1-desc">A comprehensive SOC 1 examination preparation and support framework covering all SSAE 18 requirements</p>
                        </div>
                    </ScrollReveal>
                    
                    {/* Row 1: Steps 1–3 */}
                    <ScrollReveal direction="up" delay={100}>
                        <div className="soc1-fw-row">
                            {components.slice(0, 3).map((step, index) => (
                                <div className="soc1-fw-card" key={index}>
                                    <div className="soc1-fw-badge">{String(index + 1).padStart(2, '0')}</div>
                                    {index < 2 && <span className="soc1-fw-connector" />}
                                    <div className="soc1-fw-icon">{step.icon}</div>
                                    <h3 className="soc1-fw-title">{step.title}</h3>
                                    <p className="soc1-fw-description">{step.desc}</p>
                                </div>
                            ))}
                        </div>
                    </ScrollReveal>

                    {/* Row 2: Steps 4–6 */}
                    <ScrollReveal direction="up" delay={200}>
                        <div className="soc1-fw-row-2">
                            {components.slice(3, 6).map((step, index) => (
                                <div className="soc1-fw-card" key={index + 3}>
                                    <div className="soc1-fw-badge">{String(index + 4).padStart(2, '0')}</div>
                                    {index < 2 && <span className="soc1-fw-connector" />}
                                    <div className="soc1-fw-icon">{step.icon}</div>
                                    <h3 className="soc1-fw-title">{step.title}</h3>
                                    <p className="soc1-fw-description">{step.desc}</p>
                                </div>
                            ))}
                        </div>
                    </ScrollReveal>
                </div>
            </section>

            {/* SECTION A: OFF-WHITE */}
            <section className="soc1-sec soc1-bg-offwhite">
                <div className="soc1-container">
                    <ScrollReveal direction="up">
                        <div className="soc1-hdr">
                            <p className="soc1-eyebrow-red">Our Approach</p>
                            <h2 className="soc1-title">Our Examination Methodology</h2>
                            <p className="soc1-desc">A proven 10-step methodology from baseline readiness assessment through SOC 1 report issuance</p>
                        </div>
                    </ScrollReveal>
                    
                    <div className="soc1-methodology-timeline">
                        <div className="soc1-methodology-line" />
                        <div className="soc1-methodology-items">
                            {approach.map((a, i) => (
                                <ScrollReveal key={i} direction={i % 2 === 0 ? 'left' : 'right'} delay={100}>
                                    <div className={`soc1-methodology-item ${i % 2 === 0 ? 'left' : 'right'}`}>
                                        <div className="soc1-methodology-card">
                                            <div className="soc1-methodology-card-num">{String(i + 1).padStart(2, '0')}</div>
                                            <div className="soc1-methodology-card-header">
                                                <div className="soc1-methodology-card-icon">{a.icon}</div>
                                                <h3 className="soc1-methodology-card-title">{a.title}</h3>
                                            </div>
                                            <p className="soc1-methodology-card-desc">{a.desc}</p>
                                        </div>
                                        <div className="soc1-methodology-dot-connector" />
                                    </div>
                                </ScrollReveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION B: WHITE + SUBTLE GRID */}
            <section className="soc1-sec soc1-bg-white-grid">
                <div className="soc1-grid-overlay" />
                <div className="soc1-container">
                    <ScrollReveal direction="up">
                        <div className="soc1-hdr">
                            <p className="soc1-eyebrow-red">Our Checklist</p>
                            <h2 className="soc1-title">SOC 1 Coverage Checklist</h2>
                            <p className="soc1-desc">Complete coverage of SOC 1/SSAE 18 examination requirements</p>
                        </div>
                    </ScrollReveal>
                    <div className="soc1-checklist-grid">
                        {checklist.map((it, i) => (
                            <ScrollReveal key={i} direction="up" delay={i * 45}>
                                <div className="soc1-checklist-item">
                                    <div className="soc1-checklist-icon"><CheckCircle size={18} /></div>
                                    <span className="soc1-checklist-text">{it}</span>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION A: OFF-WHITE */}
            <section className="soc1-sec soc1-bg-offwhite">
                <div className="soc1-container">
                    <ScrollReveal direction="up">
                        <div className="soc1-hdr">
                            <p className="soc1-eyebrow-red">Why CyberAries</p>
                            <h2 className="soc1-title">Why Choose CyberAries?</h2>
                        </div>
                    </ScrollReveal>
                    <div className="soc1-why-grid-4">
                        {why.map((w, i) => (
                            <ScrollReveal key={i} direction="up" delay={i * 100}>
                                <div className="soc1-why-clean-card">
                                    <div className="soc1-why-clean-icon-box">{w.icon}</div>
                                    <h3 className="soc1-why-clean-title">{w.title}</h3>
                                    <p className="soc1-why-clean-desc">{w.desc}</p>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CYBERARIES DIFFERENCE SECTION */}
            <section className="soc1-diff soc1-bg-white-grid">
                <div className="soc1-grid-overlay" />
                <div className="soc1-container">
                    <ScrollReveal direction="up">
                        <div className="soc1-hdr">
                            <p className="soc1-eyebrow-red">Our Difference</p>
                            <h2 className="soc1-diff-title">THE <span className="soc1-diff-highlight">CYBERARIES</span> DIFFERENCE</h2>
                        </div>
                    </ScrollReveal>
                    <div className="soc1-diff-grid">
                        <ScrollReveal animation="fade-right" delay={100}>
                            <div className="soc1-diff-card soc1-diff-card--traditional">
                                <h3 className="soc1-diff-heading">Typical SOC Consultants</h3>
                                <ul className="soc1-diff-list">
                                    {trad.map((item, i) => (
                                        <li key={i} className="soc1-diff-item">
                                            <span className="soc1-diff-bullet">•</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </ScrollReveal>

                        <ScrollReveal animation="fade-left" delay={200}>
                            <div className="soc1-diff-card soc1-diff-card--featured">
                                <h3 className="soc1-diff-heading soc1-diff-heading--ca">
                                    CyberAries SOC 1 Practice
                                </h3>
                                <ul className="soc1-diff-list">
                                    {ca.map((item, i) => (
                                        <li key={i} className="soc1-diff-item">
                                            <span className="soc1-diff-bullet">
                                                <CheckCircle size={18} className="soc1-check-icon" />
                                            </span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </ScrollReveal>
                    </div>
                </div>
            </section>

            {/* DELIVERABLES SECTION */}
            <section className="soc1-sec soc1-bg-white-grid">
                <div className="soc1-grid-overlay" />
                <div className="soc1-container">
                    <ScrollReveal direction="up">
                        <div className="soc1-hdr">
                            <p className="soc1-eyebrow-red">Our Deliverables</p>
                            <h2 className="soc1-title">Comprehensive Deliverables</h2>
                            <p className="soc1-desc">End to end documentation, controls, and reporting to ensure SOC 1 compliance and strengthen your security posture.</p>
                        </div>
                    </ScrollReveal>
                    <div className="soc1-pillar-grid">
                        {deliverables.map((d, i) => (
                            <ScrollReveal key={i} direction="up" delay={i * 100}>
                                <div className="soc1-pillar-card">
                                    <span className="soc1-pillar-bg-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                                    <div className="soc1-pillar-top">
                                        <div className="soc1-pillar-icon-wrap">
                                            {d.icon}
                                        </div>
                                        <div>
                                            <span className="soc1-pillar-num">{String(i + 1).padStart(2, '0')}</span>
                                            <h4 className="soc1-pillar-title">{d.title}</h4>
                                        </div>
                                    </div>
                                    <div className="soc1-pillar-divider" />
                                    <ul className="soc1-pillar-list">
                                        {d.items.map((item, idx) => (
                                            <li key={idx}>{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA SECTION */}
            <section className="soc1-cta-section" style={{ backgroundImage: `url(${ctaBgImage})` }}>
                <div className="soc1-container">
                    <ScrollReveal direction="up">
                        <div className="soc1-cta-content">
                            <h2 className="soc1-cta-title">
                                <span className="soc1-grad">Ready to Achieve Your SOC 1 Attestation Report?</span>
                            </h2>
                            <p className="soc1-cta-description">
                                Demonstrate financial reporting control assurance to your customers and auditors with a clean SOC 1 Type I or Type II report.
                            </p>
                            <div className="soc1-cta-buttons">
                                <Link to="/contact" className="soc1-btn soc1-cta-btn">
                                    <span>Request SOC 1 Assessment</span>
                                    <ArrowRight size={18} className="soc1-btn-icon" />
                                </Link>
                            </div>
                        </div>
                    </ScrollReveal>
                </div>
            </section>
        </div>
    );
};

export default SOC1;
